import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-apollo';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import Dropzone from 'react-dropzone';
import { useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import {
  Form, FormGroup, Label, Input, ButtonGroup, FormText, Button,
} from 'reactstrap';
import * as _ from 'lodash';
import PropTypes from 'prop-types';
import {
  customSelectStyles, validationMessages, regexPattern,
  profileImageValidation, staticConstants, getLocalUserData, NUMERIC_VALUE, contactNumberValidation, selectValidation, mapTagsFromAPIRequest,
} from '../../../utils';
import InputError from '../elments/InputError';
import 'react-phone-input-2/lib/style.css';
import ImageCropper from '../../modals/ImageCropper';
import { Country, State, City } from '../region';
import { GET_GENRE } from '../../../graphql-client/queries';
import { TermsService, PrivacyPolicy } from '../../modals';
import { useScrollToError } from '../../../hooks/useScrollToError';

const ManagerRegisterForm = (props) => {
  const { invitationData = null } = props;
  const userData = getLocalUserData();
  const {
    register, handleSubmit, errors, setValue, clearError, setError, triggerValidation,
  } = useForm({ mode: 'onBlur', nativeValidation: false });

  useScrollToError(errors);

  const { data: generData } = useQuery(GET_GENRE);
  const [form, setState] = useState({
    country: null,
    state: null,
    city: null,
    contactNumber: '',
  });
  const [genre, setGenere] = useState(null);
  const [isOpenCropModal, setIsOpenCropModal] = useState(false);
  const [image, setImage] = useState();
  const [artistImage, setArtistImage] = useState();
  const [managerImage, setManagerImage] = useState();
  const [modalObj, setModalObj] = useState({});
  const [termsServiceModal, setTermsServiceModal] = useState(false);
  const [privacyPolicyModal, setPrivacyPolicyModal] = useState(false);

  const toggleTermsServiceModal = () => setTermsServiceModal(!termsServiceModal);
  const togglePrivacyPolicyModal = () => setPrivacyPolicyModal(!privacyPolicyModal);

  useEffect(() => {
    if (genre && props.data) {
      const genreData = [];
      genre && genre.forEach((ele) => {
        if (props.data.genre.includes(ele.value)) {
          genreData.push(ele);
        }
      });
      setValue([{ genre: genreData }]);
      setState({
        ...form, ...{ genre: genreData },
      });
    }
  }, [genre]);

  useEffect(() => {
    generData && generData.getGenre
      && setGenere(mapTagsFromAPIRequest(generData.getGenre.data));
  }, [generData]);

  const mapProfileData = useCallback((currentUserData) => {
    currentUserData.country && delete currentUserData.country.__typename;
    currentUserData.state && delete currentUserData.state.__typename;
    currentUserData.city && delete currentUserData.city.__typename;
    const preFilledData = {
      country: currentUserData.country,
      state: currentUserData.state,
      city: currentUserData.city,
      contactNumber: currentUserData.contactNumber,
    };

    setValue([
      { country: currentUserData.country },
      { state: currentUserData.state },
      { city: currentUserData.city },
      { name: currentUserData.name },
      { contactNumber: currentUserData.contactNumber },
      { bio: currentUserData.bio },
      { managerFirstName: currentUserData.managerFirstName || '' },
      { managerLastName: currentUserData.managerLastName || '' },
      { contactEmail: currentUserData.email },
      { companyName: currentUserData.companyName || '' },
      { profileImage: currentUserData.profileImage },
      { managerImage: currentUserData.managerImage },
      { instagram: currentUserData.socialMedia.instagram || '' },
      { facebook: currentUserData.socialMedia.facebook || '' },
      { spotify: currentUserData.socialMedia.spotify || '' },
      { youtube: currentUserData.socialMedia.youtube || '' },
      { twitter: currentUserData.socialMedia.twitter || '' },
      { website: currentUserData.socialMedia.website || '' },
    ]);

    setArtistImage(currentUserData.artistImage);
    setManagerImage(currentUserData.managerImage);
    clearError(['country', 'city', 'genre']);
    setValue('country', preFilledData.country);
    setValue('city', preFilledData.city);
    setValue('contactNumber', preFilledData.contactNumber);
    triggerValidation('contactNumber');
    setState({
      ...form, ...preFilledData,
    });
  }, [clearError, form, setValue, triggerValidation]);
  useEffect(() => {
    contactNumberValidation(register, 'contactNumber');
    selectValidation(register, 'country');
    selectValidation(register, 'genre');
    register({ name: 'state' });
    register({ name: 'city' });
    // added suffix 2 to remove duplicacy as it's being used in registform
    profileImageValidation(register, 'artistImageUpload2');
    profileImageValidation(register, 'managerImageUpload');
    if (props && props.data) {
      mapProfileData(props.data);
    }
    if (props && props.invitationData) {
      mapProfileData(props.invitationData);
    }
  }, [props]);

  const handleContactNumberChange = (phone) => {
    setState({
      ...form,
      contactNumber: phone,
    });

    setValue('contactNumber', phone);
    triggerValidation('contactNumber');
  };

  const handleCountryChange = (selectedOption) => {
    clearError('country');
    setValue('country', selectedOption);
    setValue('state', null);
    setValue('city', null);
    setState({
      ...form,
      country: selectedOption,
      state: {},
      city: {},
    });
  };

  const handleStateChange = (selectedOption) => {
    clearError('state');
    setValue('state', selectedOption);
    setValue('city', null);
    setState({
      ...form,
      state: selectedOption,
      city: {},
    });
  };

  const handleCityChange = (selectedOption) => {
    clearError('city');
    setValue('city', selectedOption);
    setState({
      ...form,
      city: selectedOption,
    });
  };

  const handleGenreChange = (selectedOption) => {
    setState({
      ...form,
      genre: selectedOption,
    });
    selectedOption && selectedOption.length && clearError('genre');
    !selectedOption && setError('genre');
    setValue('genre', selectedOption);
  };

  const handleImageSubmit = async (e, fieldName) => {
    const profileImageUpload = e[0];
    setValue(fieldName, e[0]);
    const success = await triggerValidation(fieldName);
    if (success) {
      setImage(profileImageUpload);
      setIsOpenCropModal(true);
      setModalObj({
        callback: fieldName === 'managerImageUpload'
          ? (img) => { setManagerImage(img); } : (img) => {
            setArtistImage(img);
          },
      });
    }
  };

  const onSubmit = (data) => {
    const registerData = _.pick(data,
      ['name', 'bio', 'email', 'managerFirstName',
        'managerLastName', 'companyName', 'contactNumber', 'artistImage', 'managerImage']);
    if (!artistImage) {
      setError('artistImageUpload2', 'required');
      return;
    }
    if (!managerImage) {
      setError('managerImageUpload', 'required');
      return;
    }
    registerData.city = data.city || { label: null, value: null };
    registerData.state = data.state || { label: null, value: null };
    registerData.country = data.country;
    registerData.genre = data.genre.map((el) => el.value);
    registerData.socialMedia = _.pick(data, ['facebook', 'spotify', 'instagram', 'youtube', 'twitter', 'website']);
    registerData.artistImage = artistImage;
    registerData.managerImage = managerImage;
    if (!userData) {
      registerData.email = data.contactEmail;
      registerData.role = staticConstants.ROLE.MANAGER;
    }
    props.submitCallBack(registerData);
  };

  const hideFileSelectModal = () => {
    setIsOpenCropModal(false);
  };
  return (
    <div className="signup-form">
      <div className={`signup-form-image-section ${errors.artistImageUpload2 ? 'image-error' : ''}`}>
        <Dropzone onDrop={(acceptedFiles) => handleImageSubmit(acceptedFiles, 'artistImageUpload2')}>
          {({ getRootProps, getInputProps }) => (
            <section>
              <div {...getRootProps()} className="signup-form-image-section">
                <input name="artistImageUpload2" disabled={Boolean(invitationData)} {...getInputProps()} multiple={false} accept="image/*" />
                <Label className={artistImage ? 'border-0' : ''}>
                  {artistImage ? (
                    <img
                      src={artistImage}
                      className="img-cover"
                      alt="profile"
                    />
                  )
                    : <></>}
                  <span>
                    Upload artist picture
                    {' '}
                    <p className="required">*</p>
                  </span>
                  <em className="icon icon-camera" />
                </Label>
              </div>
            </section>
          )}
        </Dropzone>
        <InputError error={errors.artistImageUpload2} max={NUMERIC_VALUE.TWO_HUNDRED} />

      </div>
      <div className="signup-form-section">
        <h2 className="heading heading-large heading-weight-semi-bold mb-1">
          Artist details
          <em className="required-icon">*</em>
        </h2>
        <p className="heading heading-regular heading-weight-regular">We would like to know about the artist you are managing.</p>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup className={errors.name ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Name"
            >
              Artist name
            </Label>
            <Input
              type="text"
              name="name"
              disabled={invitationData}
              placeholder="Enter artist name"
              onChange={(e) => { e.target.value = e.target.value.trimLeft(); }}
              innerRef={register({ required: true, maxLength: NUMERIC_VALUE.TWO_HUNDRED })}
            />
            <InputError error={errors.name} max={NUMERIC_VALUE.TWO_HUNDRED} />
          </FormGroup>
          <FormGroup className={errors.country ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Country"
            >
              Country
            </Label>
            <Country
              disabled={Boolean(invitationData)}
              onChange={handleCountryChange}
              value={form.country}
            />
            <InputError error={errors.country} maxLength={NUMERIC_VALUE.TWO_HUNDRED} minLength={NUMERIC_VALUE.THREE} />
          </FormGroup>
          <FormGroup className={errors.state ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="State"
            >
              State
            </Label>
            <State
              disabled={Boolean(invitationData)}
              onChange={handleStateChange}
              country={form.country}
              value={form.state}
            />
            <InputError error={errors.state} />
          </FormGroup>
          <FormGroup className={errors.city ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="City"
            >
              City
            </Label>
            <City
              disabled={Boolean(invitationData)}
              onChange={handleCityChange}
              state={form.state}
              value={form.city}
            />
            <InputError error={errors.city} />

          </FormGroup>
          <FormGroup className={errors.genre ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Genre"
            >
              Genre
            </Label>
            <Select
              disabled={Boolean(invitationData)}
              className="reactSelect multiSelect"
              styles={customSelectStyles}
              name="genre"
              placeholder="Enter genre"
              value={form.genre}
              options={genre}
              onChange={handleGenreChange}
              isMulti
            />
            <InputError error={errors.genre} />
          </FormGroup>
          <FormGroup className={errors.bio ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Bio"
            >
              A short bio
            </Label>
            <Input
              disabled={Boolean(invitationData)}
              type="textarea"
              className="medium"
              name="bio"
              placeholder="Enter a short bio"
              maxLength={NUMERIC_VALUE.FIVE_HUNDRED}
              innerRef={register({ required: true })}
            />
            <InputError error={errors.bio} max={NUMERIC_VALUE.FIVE_HUNDRED} />
          </FormGroup>
          <div className="signup-form-heading">
            <h2 className="heading heading-large heading-weight-semi-bold mb-1">
              Label contact details
              <em className="required-icon">*</em>
            </h2>
            <p className="heading heading-regular heading-weight-regular">How should we contact you?</p>
          </div>
          <div className={`signup-form-image-small ${errors.managerImageUpload ? 'image-error' : ''}`}>

            <Dropzone onDrop={(acceptedFiles) => handleImageSubmit(acceptedFiles, 'managerImageUpload')}>
              {({ getRootProps, getInputProps }) => (
                <section>
                  <div {...getRootProps()}>
                    <input name="managerImageUpload" {...getInputProps()} multiple={false} accept="image/*" />
                    <Label>
                      {
                        managerImage && (
                          <img
                            src={managerImage}
                            className="img-cover"
                            alt="profile"
                          />
                        )
                      }
                      <span className="icon icon-camera" />
                    </Label>
                  </div>
                </section>
              )}
            </Dropzone>
            <span className="icon icon-round" />
            <span className="upload-text">Upload label picture</span>
            <InputError error={errors.managerImageUpload} />

          </div>
          <FormGroup className={errors.managerFirstName ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="firstName"
            >
              First name
            </Label>
            <Input
              type="text"
              name="managerFirstName"
              placeholder="Enter first name"
              onChange={(e) => { e.target.value = e.target.value.trimLeft(); }}
              innerRef={register({ required: true, maxLength: NUMERIC_VALUE.TWO_HUNDRED })}
            />
            <InputError error={errors.managerFirstName} max={NUMERIC_VALUE.TWO_HUNDRED} />
          </FormGroup>
          <FormGroup className={errors.managerLastName ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="lastName"
            >
              Last name
            </Label>
            <Input
              type="text"
              name="managerLastName"
              placeholder="Enter last name"
              onChange={(e) => { e.target.value = e.target.value.trimLeft(); }}
              innerRef={register({ required: true, maxLength: NUMERIC_VALUE.TWO_HUNDRED })}
            />
            <InputError error={errors.managerLastName} max={NUMERIC_VALUE.TWO_HUNDRED} />

          </FormGroup>
          <FormGroup className={errors.companyName ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Name"
            >
              Company name
            </Label>
            <Input
              type="text"
              name="companyName"
              placeholder="Enter company name"
              onChange={(e) => { e.target.value = e.target.value.trimLeft(); }}
              innerRef={register({ required: true, maxLength: NUMERIC_VALUE.TWO_HUNDRED })}
            />
            <InputError error={errors.companyName} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />
          </FormGroup>
          <FormGroup className={errors.contactEmail ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Email"
            >
              Email address
            </Label>
            <Input
              type="email"
              name="contactEmail"
              placeholder="Enter email address"
              disabled={userData || invitationData}
              innerRef={register({
                required: true,
                type: 'email',
                pattern: { value: regexPattern.EMAIL, message: validationMessages.EMAIL },
              })}
            />
            <InputError error={errors.contactEmail} max={50} />
          </FormGroup>
          <FormGroup className={errors.contactNumber ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="number"
            >
              Phone number
            </Label>
            <PhoneInput
              inputProps={{
                name: 'contactNumber',
                required: true,
              }}
              country="us"
              placeholder="Enter phone number"
              value={form.contactNumber}
              onChange={handleContactNumberChange}
            />

            <InputError error={errors.contactNumber} max={NUMERIC_VALUE.TWELVE} min={NUMERIC_VALUE.THREE} />
          </FormGroup>
          <div className="signup-form-heading">
            <h2 className="heading heading-large heading-weight-semi-bold mb-1">Artist social media links</h2>
            <p className="heading heading-regular heading-weight-regular">Does your artist have a strong social base? Share links.</p>
          </div>
          <FormGroup className={errors.instagram ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Instagram"
            >
              Instagram
              {' '}
              <em className="required-icon">*</em>
            </Label>
            <Input
              disabled={Boolean(invitationData)}
              type="text"
              name="instagram"
              data-label="instagram"
              onChange={(e) => { e.target.value = e.target.value.trimLeft(); }}
              innerRef={register({
                required: true,
                type: 'text',
                pattern: { value: regexPattern.INSTAGRAM, message: validationMessages.INSTAGRAM },
              })}
            />
            <InputError error={errors.instagram} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />
          </FormGroup>
          <FormGroup className={errors.facebook ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Facebook"
            >
              Facebook
            </Label>
            <Input
              disabled={Boolean(invitationData)}
              type="text"
              name="facebook"
              data-label="facebook"
              onChange={(e) => { e.target.value = e.target.value.trimLeft(); }}
              innerRef={register({
                type: 'text',
                pattern: { value: regexPattern.FACEBOOK, message: validationMessages.FACEBOOK },
              })}
            />
            <InputError error={errors.facebook} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />

          </FormGroup>
          <FormGroup className={errors.spotify ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Spotify"
            >
              Spotify/Soundcloud (Optional)
            </Label>
            <Input
              disabled={Boolean(invitationData)}
              type="text"
              name="spotify"
              data-label="spotify"
              onChange={(e) => { e.target.value = e.target.value.trimLeft(); }}
              innerRef={register({
                type: 'text',
                pattern: { value: regexPattern.WEBSITE, message: validationMessages.SPOTIFY },
              })}
            />
            <InputError error={errors.spotify} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />

          </FormGroup>
          <FormGroup className={errors.youtube ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="YouTube"
            >
              YouTube (Optional)
            </Label>
            <Input
              disabled={Boolean(invitationData)}
              type="text"
              onChange={(e) => { e.target.value = e.target.value.trimLeft(); }}
              name="youtube"
              data-label="youtube"
              innerRef={register({
                type: 'text',
                pattern: { value: regexPattern.YOUTUBE, message: validationMessages.YOUTUBE },
              })}
            />
            <InputError error={errors.youtube} max={NUMERIC_VALUE.TWO_HUNDRED} />
          </FormGroup>
          <FormGroup className={errors.twitter ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Twitter"
            >
              Twitter (Optional)
            </Label>
            <Input
              disabled={Boolean(invitationData)}
              type="text"
              name="twitter"
              data-label="twitter"
              onChange={(e) => { e.target.value = e.target.value.trimLeft(); }}
              innerRef={register({
                type: 'text',
                pattern: { value: regexPattern.TWITTER, message: validationMessages.TWITTER },
              })}
            />
            <InputError error={errors.twitter} max={NUMERIC_VALUE.TWO_HUNDRED} />
          </FormGroup>
          <FormGroup className={errors.website ? `${staticConstants.ERROR_CLASS.FORM_ERROR}` : ''}>
            <Label
              for="Website"
            >
              Website (Optional)
            </Label>
            <Input
              disabled={Boolean(invitationData)}
              type="text"
              name="website"
              data-label="website"
              onChange={(e) => { e.target.value = e.target.value.trimLeft(); }}
              innerRef={register({
                type: 'text',
                pattern: { value: regexPattern.WEBSITE, message: validationMessages.WEBSITE },
              })}
            />
            <InputError error={errors.website} max={NUMERIC_VALUE.TWO_HUNDRED} />
          </FormGroup>
          <ButtonGroup>
            <Button
              color="primary"
              size="lg"
              element="Block"
              type="submit"
            >
              {!userData ? staticConstants.BUTTON_TITLE.CREATE : staticConstants.BUTTON_TITLE.UPDATE}
            </Button>
          </ButtonGroup>
          {!userData
            && (
              <FormText color="theme">
                By signing up you agree to BYGmusic’s
                {' '}
                <Link to="/" onClick={toggleTermsServiceModal}>Terms of Service</Link>
                {' '}
                &
                {' '}
                <Link to="/" onClick={togglePrivacyPolicyModal}>Privacy policy</Link>
              </FormText>
            )}
        </Form>
      </div>
      {isOpenCropModal && (
        <ImageCropper
          isOpen={isOpenCropModal}
          image={image}
          savedCroppedImage={modalObj.callback}
          onClose={hideFileSelectModal}
        />
      )}
      {termsServiceModal && (
        <TermsService
          isOpen={termsServiceModal}
          onClose={() => { toggleTermsServiceModal(); }}
        />
      )}
      {privacyPolicyModal && (
        <PrivacyPolicy
          isOpen={privacyPolicyModal}
          onClose={() => { togglePrivacyPolicyModal(); }}
        />
      )}
    </div>
  );
};
ManagerRegisterForm.propTypes = {
  toggle: PropTypes.func.isRequired,
  submitCallBack: PropTypes.func.isRequired,
  data: PropTypes.object,
  invitationData: PropTypes.object,
};
ManagerRegisterForm.defaultProps = {
  data: null,
  invitationData: null,
};
export default ManagerRegisterForm;
